import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { CurrentUser } from '@qiasphere/interfaces';

import { HttpResponseCodes } from '../data/constants';
import { OrganizationRoleEnum } from '../enums/organization-roles.enum';
import { AuthService } from '../services/auth.service';
import { DialogService } from '../services/dialog.service';

export enum AuthErrorCode {
  UNKNOWN = 'UNKNOWN',
  USER_NOT_LOGGED_IN = 'USER_NOT_LOGGED_IN',
  USER_NOT_IN_A_ORGANIZATION = 'USER_NOT_IN_A_ORGANIZATION',
  USER_HAS_NO_ROLE_IN_ORGANIZATION = 'USER_HAS_NO_ROLE_IN_ORGANIZATION',
  USER_EMAIL_DOMAIN_BLACKLISTED = 'USER_EMAIL_DOMAIN_BLACKLISTED',
  USER_INACTIVE = 'USER_INACTIVE',
  TAC_NOT_ACCEPTED = 'TAC_NOT_ACCEPTED'
}

export enum IrcAuthErrorCode {
  UNKNOWN = 'UNKNOWN',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  REMOTE_SETTINGS_DISABLED = 'REMOTE_SETTINGS_DISABLED',
  INSUFFICIENT_ROLE = 'INSUFFICIENT_ROLE',
  USER_INACTIVE = 'USER_INACTIVE'
}

export const SKIP_AUTH_CREDENTIALS = new HttpContextToken<boolean>(() => false);

const IRC_URL_PATTERN = /device\/.+\/irc/;

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const dialogService = inject(DialogService);

  return authService.getAuthBearer().pipe(
    switchMap(bearer => {
      const headers = bearer ? request.headers.set('Authorization', `Bearer ${bearer}`) : request.headers;
      const withCredentials = !request.context.get(SKIP_AUTH_CREDENTIALS);
      const requestClone = request.clone({ withCredentials, headers });

      if (request.url.indexOf('/current-user') !== -1 && authService.isServiceUser) {
        return next(requestClone).pipe(
          tap(event => {
            if (event instanceof HttpResponse) {
              const user = event.body as CurrentUser;
              if (!user.roles.some(role => role === OrganizationRoleEnum.SERVICE_ACCOUNT_ADMIN)) {
                throw Object.assign(new HttpErrorResponse({ status: 403 }));
              }
            }
            return event;
          })
        );
      }

      return next(requestClone).pipe(
        catchError(err => {
          if (err.status) {
            switch (err.status) {
              case HttpResponseCodes.UNAUTHORIZED:
                handleUnauthorized(request.url, authService, dialogService);
                break;
              case HttpResponseCodes.FORBIDDEN:
                handleForbidden(request.url, err, authService, router, dialogService);
                break;
            }
          }
          return throwError(() => err);
        })
      );
    })
  );
};

const handleUnauthorized = (url: string, authService: AuthService, dialogService: DialogService): void => {
  if (IRC_URL_PATTERN.test(url)) {
    dialogService.error('Unauthorized', 'Your remote configurator session has expired.');
    return;
  }
  authService.redirectToLogin();
};

const handleForbidden = (
  url: string,
  err: HttpErrorResponse,
  authService: AuthService,
  router: Router,
  dialogService: DialogService
): void => {
  if (IRC_URL_PATTERN.test(url)) {
    const errorCode = err?.error?.errorCode ?? IrcAuthErrorCode.UNKNOWN;
    console.error(`IRC Forbidden: ${errorCode}`);
    const message =
      errorCode === IrcAuthErrorCode.REMOTE_SETTINGS_DISABLED
        ? 'Remote Instrument Configuration feature has been disabled on the instrument.'
        : 'You are not authorized to perform this action.';
    dialogService.refreshPage(message);
    return;
  }

  const errorCode = err?.error?.errorCode ?? AuthErrorCode.UNKNOWN;
  if (!authService.isServiceUser) {
    switch (errorCode) {
      case AuthErrorCode.USER_EMAIL_DOMAIN_BLACKLISTED:
        navigateIfNotAlready(router, ['setup', 'institutions-only']);
        break;
      case AuthErrorCode.USER_NOT_LOGGED_IN:
        navigateIfNotAlready(router, ['setup', 'license']);
        break;
      case AuthErrorCode.USER_INACTIVE:
      case AuthErrorCode.TAC_NOT_ACCEPTED:
      default:
        console.warn(`Forbidden response not handled in UI: ${errorCode}`);
        break;
    }
  } else {
    dialogService.info('You are not eligible to make this action.');
  }
};

/**
 * Skip the navigation when we are already at (or in-flight toward) the target
 * route. Prevents the cancel-and-restart cascade where `403 USER_NOT_LOGGED_IN`
 * on the destination page re-triggers a navigation to the same destination.
 *
 * Both checks are needed:
 *   - `router.url` reflects only the last *completed* navigation, so during
 *     the initial navigation to /setup/license it is still '/' or empty.
 *   - `router.getCurrentNavigation()` exposes the in-flight target URL, which
 *     is what we actually care about during the very first guard evaluation.
 */
const navigateIfNotAlready = (router: Router, commands: string[]): void => {
  const target = '/' + commands.join('/');
  if (urlMatchesTarget(router.url, target)) return;
  if (urlMatchesTarget(inFlightNavigationUrl(router), target)) return;
  router.navigate(commands, { replaceUrl: true });
};

const urlMatchesTarget = (url: string | undefined, target: string): boolean => {
  if (!url) return false;
  return url === target || url.startsWith(target + '/') || url.startsWith(target + '?');
};

const inFlightNavigationUrl = (router: Router): string | undefined => {
  // Angular 21 marks getCurrentNavigation() as deprecated, but the suggested
  // replacements (router.events stream) are async — unusable from inside a
  // synchronous HTTP interceptor catchError where we need the in-flight nav
  // target *right now* to decide whether to re-navigate.

  const nav = router.getCurrentNavigation();
  if (!nav) return undefined;
  return (nav.finalUrl ?? nav.extractedUrl)?.toString();
};
